import React, { useState } from 'react'
import { trans } from 'sharedKernel/i18n'
import { useSmartNavigateLegacy as useNavigate } from 'components/SmartLink'
import { DateTime } from 'luxon'
import { useSelector, TReducers } from 'store'
import { HotsiteEvent, HotsiteEventProps } from '../../store/contracts'
import { BadgeArea, Badge, BaseButton, OpenEventsCard, CardEventHeader } from 'iebt-zing-storybook'
import { Container, Row, Col } from 'react-bootstrap'
import Tippy from '@tippyjs/react'
import { followCursor } from 'tippy.js'
import {
  OpenEventsMarketplaceWrapper,
  OpenEventsMarketplaceInfoWrapper,
  OpenEventsMarketplaceTitle,
  OpenEventsMarketplaceCards,
  HotsiteCardsRow,
  HotsiteCardsColumn
} from './styles'
import { Filter } from 'components/Filter'
import { Loading } from 'components/loading'
import { useInfiniteScroll } from 'sharedKernel/hooks/infiniteScroll'
import { isCurrentDateBiggerThanDeadline } from 'sharedKernel/handlers'
import { handleApplyAction } from '../../behavior'

const LAZY_LOAD_CHUNK = 20

export const OpenEventsMarketplace = (): JSX.Element => {
  const navigate = useNavigate()

  const hotsiteEvents: HotsiteEventProps = useSelector((state: TReducers) => state.hotsiteEventsData)
  const isAuthenticated = useSelector((state: TReducers) => state.auth.isAuthenticated)
  const userPermissions = useSelector((state: TReducers) => state.auth.permissions)

  const [filterValue, setFilterValue] = useState('')
  const [totalScreenCards, setTotalScreenCards] = useState(LAZY_LOAD_CHUNK)

  const [isFetching, setIsFetching] = useInfiniteScroll(onLoadAtBottomPage, { itemsPerLoad: LAZY_LOAD_CHUNK, loadEnd: hotsiteEvents.rows?.length })

  const openEventsLabels = {
    title: trans('Confira alguns dos eventos abertos'),
    closed: trans('Encerrado'),
    apply: trans('Ver mais'),
    signUpTooltip: trans('Veja mais detalhes do evento'),
    closedTooltip: trans('A data limite foi atingida')
  }

  const onFilterChange = (keyword: string): void => {
    setFilterValue(keyword.toLowerCase())
  }

  function onLoadAtBottomPage(): void {
    setTimeout(() => {
      setTotalScreenCards(totalScreenCards + LAZY_LOAD_CHUNK)
      setIsFetching(false)
    }, 500)
  }

  const handleFilterIfValid = (keyword: string): HotsiteEvent[] => {
    if (isValidKeyword(keyword)) {
      return filterEvents(hotsiteEvents.rows ?? [])
    }
    return hotsiteEvents.rows ?? []
  }

  const isValidKeyword = (keyword: string): boolean => {
    return keyword.length >= 3
  }

  const filterEvents = (events: HotsiteEvent[]): HotsiteEvent[] => {
    return events.filter((event) =>
      event.name.toLowerCase().includes(filterValue)
    )
  }

  return (
    <OpenEventsMarketplaceWrapper fluid>
      <Row className="d-flex w-full flex-column align-items-center">
        <Col>
          <OpenEventsMarketplaceInfoWrapper>
            <OpenEventsMarketplaceTitle>{openEventsLabels.title}</OpenEventsMarketplaceTitle>
          </OpenEventsMarketplaceInfoWrapper>
        </Col>
        <Col lg={6} className="my-5">
          <Container>
            <Filter
              onFilterChange={onFilterChange}
              searchBtn={false}
            />
          </Container>
        </Col>
        <Col>
          <OpenEventsMarketplaceCards>
            <Container>
              <HotsiteCardsRow>
                {handleFilterIfValid(filterValue)?.slice(0, totalScreenCards).map((event, index) => {
                  const deadline = DateTime.fromISO(event.endDate).setLocale('pt').toLocaleString(DateTime.DATE_FULL)
                  const isClosed = isCurrentDateBiggerThanDeadline(event.endDate)
                  const tooltipContent = isClosed ? openEventsLabels.closedTooltip : openEventsLabels.signUpTooltip
                  const buttons = (
                    <Tippy
                      content={tooltipContent}
                      followCursor={true}
                      plugins={[followCursor]}
                      animation="scale"
                      arrow={false}
                      theme="primary"
                    >
                      <div>
                        {isClosed
                          ? (<BaseButton
                            btnContent={openEventsLabels.closed}
                            variant='risk-secondary'
                            size='medium'
                            onClick={() => navigate(handleApplyAction(isAuthenticated, userPermissions, event.id, true))}
                          />)
                          : (<BaseButton
                            btnContent={openEventsLabels.apply}
                            variant='risk-secondary'
                            size='medium'
                            onClick={() => navigate(handleApplyAction(isAuthenticated, userPermissions, event.id, true))}
                          />)}
                      </div>
                    </Tippy>)
                  return (
                    <HotsiteCardsColumn key={index}>
                      <OpenEventsCard
                        header={<CardEventHeader coverImage={event.style.coverImagePath} />}
                        title={event.name}
                        shortDescription={event.description}
                        badges={
                          <BadgeArea badgeQty={1}>
                            <Badge text='Data' value={deadline} icon={['fas', 'calendar-day']} type="string" />
                          </BadgeArea>
                        }
                        buttons={buttons}
                      />
                    </HotsiteCardsColumn>
                  )
                })}
              </HotsiteCardsRow>
              {isFetching === true ? <Loading /> : <></>}
            </Container>
          </OpenEventsMarketplaceCards>
        </Col>
      </Row>
    </OpenEventsMarketplaceWrapper >
  )
}
